import { motion } from 'framer-motion'

type RowStatus = 'exposed' | 'active' | 'hidden'

const catalogRows: {
  sku: string
  name: string
  category: string
  status: RowStatus
}[] = [
  { sku: 'PACK-TR24', name: 'Trail Ridge Daypack', category: 'bag_daypack', status: 'exposed' },
  { sku: 'MUG-CF04', name: 'Campfire Mug Set', category: 'kitchen_mug', status: 'exposed' },
  { sku: 'LAN-BC30', name: 'Basecamp Lantern', category: 'lighting_lantern', status: 'active' },
  { sku: 'POL-ST02', name: 'Summit Trekking Poles', category: 'hiking_poles', status: 'active' },
  { sku: 'TEN-AL2P', name: 'Alpine 2P Tent', category: 'shelter_tent', status: 'hidden' },
  { sku: 'BAG-SZ10', name: 'Sub-zero Sleeping Bag', category: 'sleep_bag', status: 'hidden' },
]

const products = [
  { name: 'Trail Ridge Daypack', variant: 'Charcoal — 24L', price: '$89.00' },
  { name: 'Campfire Mug Set', variant: 'Cream — 4pk', price: '$28.00' },
]

const staggerUI = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.12 } },
}

const rowFade = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function StatusChip({ status }: { status: RowStatus }) {
  if (status === 'exposed') {
    return (
      <span className="shrink-0 rounded-full bg-oak-green/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-oak-green">
        Discoverable
      </span>
    )
  }
  if (status === 'active') {
    return (
      <span className="shrink-0 rounded-full bg-oak-black/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-oak-black/50">
        Active
      </span>
    )
  }
  return (
    <span className="shrink-0 rounded-full bg-oak-black/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-oak-black/40">
      Hidden
    </span>
  )
}

export default function ControlMockup() {
  return (
    <div className="relative">
      {/* Catalog panel */}
      <motion.div
        variants={staggerUI}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="w-full overflow-hidden rounded-2xl border border-oak-black/[0.06] bg-white p-4 md:w-[62%] md:p-5"
        style={{
          boxShadow: '0 4px 16px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.05)',
        }}
      >
        <motion.div variants={rowFade} className="flex items-center gap-1.5">
          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-oak-green text-[9px] font-bold text-white">
            T
          </div>
          <span className="text-[13px] font-semibold text-oak-black">
            Product catalog
          </span>
        </motion.div>

        <div className="mt-3 space-y-1.5">
          {catalogRows.map((r) => (
            <motion.div
              key={r.sku}
              variants={rowFade}
              className={`grid grid-cols-[32px_1fr_auto] items-center gap-3 rounded-lg border px-2.5 py-2 ${
                r.status === 'exposed'
                  ? 'border-oak-green/40 bg-oak-green/[0.04]'
                  : r.status === 'hidden'
                    ? 'border-oak-black/[0.04] bg-oak-black/[0.015] opacity-50'
                    : 'border-oak-black/[0.06] bg-white'
              }`}
            >
              <div className="h-8 w-8 rounded-md bg-oak-cream" />
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-semibold leading-tight text-oak-black">
                  {r.name}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[9.5px] text-oak-black/45">
                  <span className="font-mono">{r.sku}</span>
                  <span className="text-oak-black/20">·</span>
                  <span className="truncate">{r.category}</span>
                </div>
              </div>
              <StatusChip status={r.status} />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Chat panel — overlapping top-right on md+ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
          duration: 0.6,
          delay: 0.45,
          ease: [0.38, 0.005, 0.215, 1] as const,
        }}
        className="mt-4 w-full overflow-hidden rounded-2xl border border-oak-black/[0.06] bg-white p-4 md:absolute md:right-0 md:top-10 md:mt-0 md:w-[44%] lg:-right-6"
        style={{
          boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05)',
        }}
      >
        <div className="text-center text-[13px] font-semibold text-oak-black">
          AI Assistant
        </div>

        <div className="mt-3 flex justify-end">
          <div className="max-w-[88%] rounded-xl bg-gold-50 px-3 py-2 text-[11px] leading-[1.4] text-oak-black">
            Starting my first backcountry trip in the Rockies — any must-haves?
          </div>
        </div>

        <div className="mt-2 flex justify-start">
          <div className="max-w-[92%] rounded-xl border border-oak-black/[0.06] bg-white px-3 py-2 text-[11px] leading-[1.4] text-oak-black/80">
            Absolutely. A couple of community favorites to get you started:
          </div>
        </div>

        <div className="mt-2.5 grid grid-cols-2 gap-2">
          {products.map((p) => (
            <div
              key={p.name}
              className="flex flex-col overflow-hidden rounded-lg border border-oak-green/40 bg-white"
            >
              <div className="aspect-square bg-oak-cream" />
              <div className="px-2 py-2">
                <div className="text-[10.5px] font-semibold leading-tight text-oak-black">
                  {p.name}
                </div>
                <div className="text-[10px] text-oak-black/50">{p.variant}</div>
                <div className="mt-1 text-[11px] font-semibold text-oak-black">
                  {p.price}
                </div>
                <div className="mt-1.5 flex items-center gap-1">
                  <div className="flex h-3 w-3 items-center justify-center rounded-full bg-oak-green text-[7px] font-bold text-white">
                    T
                  </div>
                  <span className="text-[9.5px] text-oak-black/60">
                    Trailhead Co.
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between rounded-lg border border-oak-black/[0.06] px-3 py-2 text-[11px] text-oak-black/40">
          <span>Type your message</span>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 13V3M3 8l5-5 5 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </motion.div>
    </div>
  )
}
